import React, { useState } from 'react';
import { Card, CardHeader, Box, Divider, Grid, Button } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import CloseIcon from '@material-ui/icons/Close';
import { FilterChip, QuickSearch, useAPI, VerticalBoxContainer } from '@shared/components';
import ExcelExportButton from '../../../../components/ExcelExportButton';
import axios from '../../../../utils/axios';
import useGlobalAlertContext from '../../../../hooks/useGlobalAlertContext';
import getChipValue from '../../../../utils/getChipValue';
import { handleChipDelete } from '../../../../utils/filterHelpers';
import { handleSortChange, getSortDirection } from '../../../../utils/sortHelpers';
import List from './Table';
import Modal from './Modal';
import Filter from './Filter';

const tableColumns = [
  {
    name: 'Korisnik kreirao',
    value: 'createdBy',
    align: 'left',
    width: 150,
    required: true,
    placeholder: 'korisniku',
  },
  {
    name: 'Komentar',
    value: 'subjectComment',
    align: 'left',
    required: true,
    width: 300,
    placeholder: 'komentaru',
  },
  {
    name: 'Datum kreiranja',
    value: 'dateCreated',
    align: 'left',
    required: true,
    width: 100,
    placeholder: 'datumu kreiranja',
  },
];

const exportFields = [
  {
    label: 'Korisnik kreirao',
    value: 'createdBy',
  },
  {
    label: 'Komentar',
    value: 'subjectComment',
  },
  {
    label: 'Datum kreiranja',
    value: 'dateCreated',
    type: 'dateTime',
  },
];

const getLabelText = (value) => {
  const map = {
    createdBy: 'Korisnik kreirao',
    subjectComment: 'Komentar',
    dateCreated: 'Datum kreiranja',
  };
  return map[value];
};

const Comments = ({ subjectId, insertAllowed }) => {
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({});
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sorters, setSorters] = useState([]);
  const [selectedComment, setSelectedComment] = useState(undefined);
  const [modalVisible, setModalVisible] = useState(false);
  const { sendSuccessAlertData, sendErrorAlertData } = useGlobalAlertContext();
  const [triggerRerender, setRerender] = useState(false);

  const [comments = { nodes: [], totalCount: 0 }, error, loading] = useAPI(
    () =>
      axios
        .get(`subject/${subjectId}/comments`, {
          params: {
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            conditions: filters,
            orderBy: [...sorters, '"id" desc'],
          },
        })
        .then(({ data }) => data),
    [page, rowsPerPage, filters, sorters, triggerRerender],
  );

  return (
    <VerticalBoxContainer>
      <Filter
        currentValues={filters}
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        setFilters={setFilters}
        setPage={setPage}
        getLabelText={getLabelText}
      />
      <Modal
        visible={modalVisible}
        setVisible={setModalVisible}
        selectedComment={selectedComment}
        setSelectedComment={setSelectedComment}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        resetComment={() => {
          setSelectedComment(undefined);
        }}
        setRerender={setRerender}
        subjectId={subjectId}
      />
      <Box width="100%">
        <Card>
          <CardHeader
            title={
              <QuickSearch
                name="subjectComment"
                placeholder="Brza pretraga po komentaru"
                setFilters={setFilters}
                mainFilter={filters}
                setPage={setPage}
              />
            }
            action={
              <Grid container spacing={4}>
                <Grid item>
                  <Button
                    color="primary"
                    disabled={Object.keys(filters).filter((k) => filters[k])?.length === 0}
                    startIcon={<CloseIcon fontSize="small" />}
                    onClick={() => setFilters({})}
                  >
                    <Box fontSize="small">Ukloni sve filtere</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon fontSize="small" />}
                    onClick={() => setFilterModalVisible(true)}
                  >
                    <Box fontSize="small">Filteri</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <ExcelExportButton
                    totalCount={comments.totalCount}
                    sortById={false}
                    variant="outlined"
                    fields={exportFields}
                    sorters={[...sorters, '"id" desc']}
                    filters={filters}
                    routeName={`subject/${subjectId}/comments`}
                    fileName={`komentari-predmeta-${subjectId}`}
                  />
                </Grid>
                {insertAllowed && (
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddCircleOutlineOutlinedIcon />}
                      onClick={() => setModalVisible(true)}
                    >
                      <Box fontSize="small">Novi komentar</Box>
                    </Button>
                  </Grid>
                )}
              </Grid>
            }
          />
          <Divider />
          {Object.keys(filters).filter((k) => filters[k])?.length !== 0 && (
            <>
              <Box ml={2} p={2} display="flex" alignItems="center" flexWrap="wrap">
                {Object.keys(filters).map(
                  (chip) =>
                    filters[chip] !== undefined && (
                      <FilterChip
                        color="primary"
                        variant="outlined"
                        key={chip}
                        label={`${getLabelText(chip)}: ${getChipValue(chip, filters)}`}
                        onDelete={() => handleChipDelete(chip, setFilters, setPage)}
                      />
                    ),
                )}
              </Box>
            </>
          )}
          <List
            loading={loading}
            error={error}
            comments={comments.nodes}
            tableColumns={tableColumns}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalCount={comments.totalCount}
            handleSortChange={handleSortChange}
            getSortDirection={getSortDirection}
            setSorters={setSorters}
            sorters={sorters}
          />
        </Card>
      </Box>
    </VerticalBoxContainer>
  );
};

export default Comments;
